/**
 * Per-session browser-side logger.
 *
 * Buffers events in memory and ships them to the backend
 * `POST /v1/sessions/{session_id}/client-log` endpoint either via fetch
 * (during the call) or `navigator.sendBeacon` (on tab close / pagehide
 * — the only path guaranteed to reach the server when the browser is
 * tearing down).
 *
 * Each entry carries:
 *   - `ts`           — high-resolution wall-clock ISO string at log time
 *   - `mono_ms`      — `performance.now()` offset since logger init
 *   - `type`         — short event tag (ws_open, ws_close, user_action, …)
 *   - `data`         — arbitrary JSON payload
 *   - `visibility`   — document.visibilityState at log time
 *   - `online`       — navigator.onLine at log time
 */

type LogLevel = "INFO" | "WARN" | "ERROR" | "DEBUG";

type LogEntry = {
  ts: string;
  mono_ms: number;
  level: LogLevel;
  type: string;
  data?: any;
  visibility?: string;
  online?: boolean;
};

const ORIGIN_T0 = performance.now();

class ClientLogger {
  private sessionId: string | null = null;
  private buffer: LogEntry[] = [];
  private endpointBase: string = "";
  private flushing = false;
  private autoFlushTimer: number | null = null;
  private readonly autoFlushMs = 5000;
  private readonly flushSizeThreshold = 25;

  setSessionId(id: string) {
    this.sessionId = id;
    this.log("logger_session_bound", { session_id: id });
    this.scheduleAutoFlush();
  }

  setEndpointBase(base: string) {
    this.endpointBase = base.replace(/\/$/, "");
  }

  log(type: string, data?: any, level: LogLevel = "INFO") {
    const entry: LogEntry = {
      ts: new Date().toISOString(),
      mono_ms: Math.round(performance.now() - ORIGIN_T0),
      level,
      type,
      data,
      visibility: typeof document !== "undefined" ? document.visibilityState : undefined,
      online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
    };
    this.buffer.push(entry);
    // Mirror to console for live debugging
    const consoleFn = level === "ERROR" ? console.error : level === "WARN" ? console.warn : console.log;
    // eslint-disable-next-line no-console
    consoleFn(`[client-log][${level}] ${type}`, data ?? "");
    if (this.buffer.length >= this.flushSizeThreshold) {
      void this.flush(false);
    }
  }

  warn(type: string, data?: any) { this.log(type, data, "WARN"); }
  error(type: string, data?: any) { this.log(type, data, "ERROR"); }

  private scheduleAutoFlush() {
    if (this.autoFlushTimer !== null) return;
    this.autoFlushTimer = window.setInterval(() => {
      if (this.buffer.length > 0) void this.flush(false);
    }, this.autoFlushMs);
  }

  private endpoint(): string | null {
    if (!this.sessionId) return null;
    const base = this.endpointBase || (import.meta.env.VITE_BACKEND_URL ?? "");
    if (!base) return null;
    return `${base.replace(/\/$/, "")}/v1/sessions/${this.sessionId}/client-log`;
  }

  /**
   * Send the current buffer to the server.
   *
   * @param useBeacon when true (during pagehide/beforeunload), uses
   *   `navigator.sendBeacon` so the request survives the page teardown.
   *   Returns immediately; we cannot tell if delivery succeeded.
   */
  async flush(useBeacon = false): Promise<void> {
    if (this.flushing && !useBeacon) return;
    if (this.buffer.length === 0) return;
    const url = this.endpoint();
    if (!url) return;
    const batch = this.buffer.splice(0, this.buffer.length);
    const payload = JSON.stringify({ entries: batch });

    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      try {
        const blob = new Blob([payload], { type: "application/json" });
        const ok = navigator.sendBeacon(url, blob);
        if (!ok) this.buffer.unshift(...batch);
      } catch {
        this.buffer.unshift(...batch);
      }
      return;
    }

    this.flushing = true;
    try {
      await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      });
    } catch (err) {
      // Re-queue on failure so we can retry later
      this.buffer.unshift(...batch);
      // eslint-disable-next-line no-console
      console.warn("[client-log] flush failed:", err);
    } finally {
      this.flushing = false;
    }
  }

  /** Stop the auto-flush timer and clear session binding. */
  reset() {
    if (this.autoFlushTimer !== null) {
      window.clearInterval(this.autoFlushTimer);
      this.autoFlushTimer = null;
    }
    this.sessionId = null;
    this.buffer = [];
  }
}

export const clientLogger = new ClientLogger();

/**
 * Install global lifecycle + error hooks once per page load. Captures:
 *   - `pagehide` / `beforeunload` → flush via Beacon
 *   - `visibilitychange` / `online` / `offline` → log transition
 *   - `error` (uncaught JS exception) → ERROR with stack
 *   - `unhandledrejection` (un-awaited promise rejection) → ERROR with stack
 */
let _hooksInstalled = false;
export function installLifecycleHooks() {
  if (_hooksInstalled || typeof window === "undefined") return;
  _hooksInstalled = true;

  const flushOnUnload = (reason: string) => {
    clientLogger.log("page_unload", { reason });
    void clientLogger.flush(true);
  };
  window.addEventListener("pagehide", () => flushOnUnload("pagehide"));
  window.addEventListener("beforeunload", () => flushOnUnload("beforeunload"));

  document.addEventListener("visibilitychange", () => {
    clientLogger.log("visibility_change", { state: document.visibilityState });
    if (document.visibilityState === "hidden") void clientLogger.flush(true);
  });

  window.addEventListener("online", () => clientLogger.log("network_online"));
  window.addEventListener("offline", () => clientLogger.warn("network_offline"));

  // Uncaught synchronous exceptions
  window.addEventListener("error", (e: ErrorEvent) => {
    clientLogger.error("js_error", {
      message: e.message,
      filename: e.filename,
      lineno: e.lineno,
      colno: e.colno,
      error_name: (e.error as any)?.name,
      stack: (e.error as any)?.stack,
    });
  });

  // Unhandled promise rejections
  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    clientLogger.error("unhandled_rejection", {
      reason: typeof e.reason === "object" ? (e.reason as any)?.message ?? String(e.reason) : String(e.reason),
      stack: (e.reason as any)?.stack,
    });
  });
}
